/* eslint-disable react-hooks/exhaustive-deps */
import moment from "moment";
import React, { useEffect, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { FaEye, FaFileAlt } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HiDocumentReport } from "react-icons/hi";
import { VscLoading } from "react-icons/vsc";
import { toast } from "react-toastify";
import addPrecision from "../../common/addPrecision";
import AppButton from "../../common/AppButton";
import AppModal from "../../common/AppModal";
import DetailBlock from "../../common/DetailBlock";
import IconButton from "../../common/IconButton";
import LabelText from "../../common/LabelText";
import SimpleExpansionPanel from "../../common/SimpleExpansionPanel";
import Table from "../../common/Table";
import Body from "../../components/Body";
import MultipleContractTaskAssignModel from "../../components/MultipleContractTaskAssignModel";
import SimpleAssignTask from "../../components/SimpleAssignTask";
import api from "../../config/api";
import useContractTasks from "../../hooks/useContractTasks";
import { observer } from "mobx-react-lite";
import { useQuery } from "@tanstack/react-query";
import {
  getContInvByContractId,
  getContractInvocies,
} from "../../stores/ContractInvocieStore";
import { getEmployees } from "../../stores/EmployeeStore";
import { getPaymentsGroupedByContract } from "../../stores/PaymentStore";
import { getContractScheduleDetails } from "../../stores/ContractStore";
import { mapJobCompletionReportData } from "../../utility/jobCompletionReportMapper";
const paymentHeader = [
  { name: "invoiceID", title: "Invoice ID" },
  { name: "paidDate", title: "Paid Date" },
  { name: "chequeNo", title: "Cheque No" },
  { name: "month", title: "Month" },
  { name: "amount", title: "Amount" },
];
function ContractsView() {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [model, setModel] = useState({
    model: "",
    visible: false,
  });
  const [isPreparingReport, setIsPreparingReport] = useState(false);

  const { data: employees } = useQuery({
    queryFn: () => getEmployees(),
    queryKey: ["employees"],
  });

  const { data: payments } = useQuery({
    queryFn: () => getContInvByContractId({ contract: state?.data._id }),
    queryKey: ["ContractInvoices", "groupedByContract"],
  });

  const handleViewContract = () => {
    navigate(
      `/contracts/view/${state?.data.organization._id}/contract/${state?.data._id}/contractPrint`,
      { state: { data: state.data } }
    );
  };
  const [contractTasks, setTasks] = useState([]);
  const tasks = useContractTasks();

  const MultiSchedule = MultipleContractTaskAssignModel();
  const handleWeekly = ({ dates }) => {
    setModel({
      model: (
        <MultiSchedule.Body
          dates={dates}
          onDone={() => {
            load();
            setModel({});
          }}
        />
      ),
      visible: true,
    });
  };

  const load = async () => {
    loadTasks();
  };

  const loadTasks = async () => {
    const res = await api.get("tasks", {
      contract: state?.data._id,
      organization: state?.data.organization._id,
    });

    if (res.ok) {
      setTasks(res.data);
    }
  };

  const handleContractReport = async () => {
    if (!state?.data?._id) {
      toast.error("Invalid contract id.");
      return;
    }

    try {
      setIsPreparingReport(true);

      const scheduleDetails = await getContractScheduleDetails(state.data._id);

      if (!scheduleDetails || !scheduleDetails.schedules) {
        toast.error("Contract schedule data is not available yet.");
        return;
      }

      const taskDetails = Object.values(scheduleDetails.schedules)
        .flatMap((dateBucket) => Object.values(dateBucket))
        .flatMap((employeeBucket) => employeeBucket.tasks || [])
        .map((task) => ({
          ...task,
          date: task.checkInTime || task.startTime,
        }));

      if (taskDetails.length === 0) {
        toast.error(
          "Task details could not be loaded for this contract. Please try again."
        );
        return;
      }

      const reportData = mapJobCompletionReportData(
        scheduleDetails,
        taskDetails
      );

      navigate(
        `/organizations/view/${state.data.organization._id}/contract/${state.data._id}/completionReportPdf`,
        {
          state: { reportData },
        }
      );
    } catch (error) {
      console.error("Contract report generation failed:", error);
      toast.error(
        "Failed to generate contract completion report. Please try again."
      );
    } finally {
      setIsPreparingReport(false);
    }
  };

  const contractX = { ...state, contractTasks };

  return (
    <Body
      title={`${state?.data?.contractId} | ${
        state?.data?.organization?.name || state?.data?.organizationName
      }`}
    >
      <div>
        <DetailBlock
          style={{ width: 1000 }}
          title="Process"
          right={
            <AppButton
              onClick={() => {
                navigate(pathname + "/edit", { state: { data: state?.data } });
              }}
              text="Edit"
              className="text-primary"
            />
          }
        >
          <div
            className="d-flex"
            style={{ justifyContent: "space-around", padding: 40 }}
          >
            <IconButton
              icon={<FaFileAlt size={70} />}
              text="Contract"
              vertical
              textStyle={{ textAlign: "center" }}
              onClick={handleViewContract}
            />
            <IconButton
              icon={
                isPreparingReport ? (
                  <VscLoading size={70} className="animate-spin" />
                ) : (
                  <HiDocumentReport size={70} />
                )
              }
              text="Completion Report"
              textStyle={{ textAlign: "center" }}
              vertical
              onClick={handleContractReport}
              loading={isPreparingReport}
              disabled={isPreparingReport}
            />
          </div>
        </DetailBlock>
        <DetailBlock style={{ width: 1000, padding: 40 }}>
          <div
            className="d-flex"
            style={{ justifyContent: "space-around", padding: 40 }}
          >
            <LabelText
              columnMode
              label="Agreement date"
              value={moment(state?.data.agreementDate).format("DD/MM/YYYY")}
            />
            <LabelText
              columnMode
              label="Start date"
              value={moment(state?.data.contractStartDate).format("DD/MM/YYYY")}
            />
            <LabelText
              columnMode
              label="End date"
              value={moment(state?.data.contractEndDate).format("DD/MM/YYYY")}
            />
          </div>
        </DetailBlock>

        {payments && (
          <div style={{ marginTop: 30, marginBottom: 30 }}>
            <DetailBlock style={{ width: 1000 }} title="Payments & Invoices">
              <div
                className="d-flex"
                style={{ flexDirection: "column", gap: "10px" }}
              >
                <Table
                  normalTable
                  tableClass="no-borders"
                  header={paymentHeader}
                  data={payments?.map((inv) => {
                    return {
                      ...inv,
                      invoiceID: inv?.invoiceID,
                      paidDate: inv.payments
                        ? moment(inv.payments?.date).format("DD/MM/YYYY")
                        : "N/A",
                      chequeNo: inv.payments
                        ? inv.payments?.chequeNumber
                        : "N/A",
                      month: moment(inv?.startDate).format("MMMM"),
                      amount: inv.payments
                        ? `$ ${addPrecision(inv.payments?.amount)}`
                        : `$ ${addPrecision(inv.total)}`,
                    };
                  })}
                  SideButtons={({ inv }) => {
                    return (
                      <div className="side-button" style={{ marginLeft: 20 }}>
                        <AppButton
                          tooltip="View"
                          onClick={() =>
                            navigate("/contracts/invoice", {
                              state: {
                                data: {
                                  ...state?.data,
                                  invoiceID: inv?.invoiceID,
                                  tax: inv?.tax,
                                  price: inv?.price,
                                  total: inv?.total,
                                  startDate: inv?.startDate,
                                  endDate: inv?.endDate,
                                },
                              },
                            })
                          }
                          leftIcon={<FaEye size={22} />}
                          className="text-primary btn-light"
                        />
                      </div>
                    );
                  }}
                />
              </div>
            </DetailBlock>
          </div>
        )}
        {/* <div style={{ marginTop: 30, marginBottom: 30 }}>
          <DetailBlock style={{ width: 1000 }} title="Tasks">
            <div
              className="d-flex"
              style={{ flexDirection: "column", gap: "10px" }}
            >
              <br />
              {contractTasks &&
                tasks.findWeeks({ contract: contractX })?.map((dt, ind) => {
                  const dates = tasks.findDays({
                    from: dt.from,
                    to: dt.to,
                    contractTasks,
                    contract: contractX,
                  });

                  const tasksx = contractTasks.filter((i) => {
                    const dtx = moment(i.date).toDate();
                    return (
                      dtx >= moment(dt.from).toDate() &&
                      dtx <= moment(dt.to).toDate()
                    );
                  });
                  const range = `${moment(dt.from).format(
                    "MMMM DD"
                  )} - ${moment(dt.to).format("MMMM DD")}`;
                  if (dates) {
                    return (
                      <SimpleExpansionPanel
                        accorditionStyle={{ width: 1000 }}
                        key={String(ind)}
                        leftTitle={range}
                        leftTitleStyle={{ maxWidth: 400 }}
                        rightTitle={
                          <AppButton
                            onClick={() => handleWeekly({ dates, range })}
                            leftIcon={<AiOutlinePlus />}
                            className="text-info "
                          />
                        }
                        rightTitleStyle={{
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                        children={
                          <SimpleAssignTask
                            refresh={() => {}}
                            employees={employees}
                            tasks={tasksx}
                          />
                        }
                      />
                    );
                  }
                  return false;
                })}
            </div>
          </DetailBlock>
        </div> */}
      </div>
      <AppModal
        onHide={() => {
          setModel({});
        }}
        {...model}
      />
    </Body>
  );
}
export default observer(ContractsView);
